import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router';
import { del, get, patch, post, put } from '../api';
import { arrow, locale, tr } from '../i18n';
import { useTenant } from '../session';
import { phoneStatus } from '../status';
import { ago, Badge, bytes, Button, Card, ConfirmDialog, CopyBox, Empty, ErrorBox, Field, friendlyError, Loading, PageHead, Tabs, useToast, when } from '../ui';
import { useAssistant } from '../assistant/panel';
import { DeviceEvents, systemName } from './devices';
import { RecoveryPointBrowser } from './restores';

/**
 * "Connect a phone": the computer half of QR pairing (docs/mobile.md, decision M8). One code signs the
 * phone in and enrolls it, so there is nothing here to type on a phone keyboard and nothing to copy
 * except as a fallback. The code lasts two minutes and is single use; asking for a new one invalidates
 * the one on screen, which is why this page asks for exactly one at a time.
 */

interface Pairing {
  id: string;
  payload: string;
  code: string;
  expiresAt: string;
  expiresInSeconds: number;
}

interface PairingStatus {
  state: 'waiting' | 'connected' | 'expired' | 'revoked';
  device: { id: string; name: string; os: string } | null;
}

export function ConnectPhone() {
  const t = useTenant();
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [connected, setConnected] = useState<PairingStatus['device']>(null);
  const timer = useRef<number | null>(null);

  const request = useCallback(async () => {
    setBusy(true);
    setError(null);
    setConnected(null);
    try {
      const p = await post<Pairing>(`/api/t/${t.tenantId}/pairings`);
      setPairing(p);
      setSecondsLeft(p.expiresInSeconds);
    } catch (e) {
      setError(e);
      setPairing(null);
    } finally {
      setBusy(false);
    }
  }, [t.tenantId]);

  // A code on a screen nobody is watching is a code nobody should be able to use: it is revoked when
  // this page is left, and it expires on its own two minutes after it appeared.
  useEffect(() => {
    return () => {
      if (pairing && !connected) void del(`/api/t/${t.tenantId}/pairings/${pairing.id}`).catch(() => undefined);
    };
  }, [connected, pairing, t.tenantId]);

  useEffect(() => {
    if (!pairing || connected) return;
    timer.current = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [connected, pairing]);

  const status = useQuery({
    queryKey: ['pairing', pairing?.id],
    enabled: !!pairing && !connected && secondsLeft > 0,
    refetchInterval: 1500,
    queryFn: () => get<PairingStatus>(`/api/t/${t.tenantId}/pairings/${pairing!.id}`),
  });

  useEffect(() => {
    if (status.data?.state === 'connected' && status.data.device) setConnected(status.data.device);
  }, [status.data]);

  const expired = !!pairing && !connected && secondsLeft === 0;

  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/devices`}>{arrow.back()} {tr('Computers and phones')}</Link>}
        title={tr('Connect a phone')}
        desc={tr('Files you add to ZeroGap on a phone are kept safe, and can appear in a folder on a computer.')}
      />

      {error ? <ErrorBox error={error} /> : null}

      <div className="stack step-card">
        <Card title={tr('1. Install ZeroGap on the phone')}>
          <p style={{ marginTop: 0 }}>{tr('Search for "ZeroGap" in the App Store or Google Play, then open it.')}</p>
        </Card>

        <Card title={tr('2. Scan this code with the phone')}>
          {connected ? (
            <div className="stack">
              <div><Badge tone="ok">{tr('Connected')}</Badge></div>
              <p style={{ margin: 0 }}>
                <strong>{connected.name}</strong> {tr('is connected and will start saving files right away.')}
              </p>
              <div className="row">
                <Link className="btn primary" to={`${t.base}/devices/${connected.id}`}>{tr('See this phone')}</Link>
                <Button onClick={request}>{tr('Connect another phone')}</Button>
              </div>
            </div>
          ) : !pairing ? (
            <div className="stack">
              <p style={{ marginTop: 0 }}>{tr('The code is good for two minutes and can be used once.')}</p>
              <div><Button variant="primary" onClick={request} disabled={busy}>{tr('Show the code')}</Button></div>
            </div>
          ) : expired ? (
            <div className="stack">
              <div><Badge tone="warn">{tr('The code expired')}</Badge></div>
              <div><Button variant="primary" onClick={request} disabled={busy}>{tr('Show a new code')}</Button></div>
            </div>
          ) : (
            <div className="stack">
              <div style={{ background: '#fff', padding: 12, width: 'fit-content', borderRadius: 8 }}>
                <QRCodeSVG value={pairing.payload} size={200} />
              </div>
              <p className="small muted" style={{ margin: 0 }}>
                {tr('In the app, choose "Scan the code on my computer". This code disappears in {n} seconds.', { n: secondsLeft })}
              </p>
              {status.isError && <p className="small muted" style={{ margin: 0 }}>{friendlyError(status.error)}</p>}
              {status.isFetching && !status.data && <Loading />}
              <CopyBox label={tr('If the camera will not work, type this in the app instead')} text={pairing.code} />
            </div>
          )}
        </Card>

        <Card title={tr('3. What the person will see')}>
          <p style={{ marginTop: 0 }}>
            {tr('The app opens on "My Files". Anything they put there - or share to ZeroGap from another app - is saved automatically.')}
          </p>
          <p className="small muted" style={{ marginBottom: 0 }}>
            {tr('Administrators of this organization can see the files people keep there, and the app says so when it is set up.')}
          </p>
        </Card>
      </div>
    </div>
  );
}

// ───────── one phone ─────────

/**
 * A phone's page. It is not a computer's page with fewer buttons: a phone has a person instead of a
 * backup plan, saves files as they arrive instead of on a schedule, and its one setting - how long a
 * deleted file can be got back - belongs to the organization (docs/mobile.md, decision M4). So there is
 * no "Back up now", no plan, and the files are shown as they are now, with what was deleted beside them.
 */
export function PhoneDetail({ data, onChanged }: { data: any; onChanged: () => void }) {
  const t = useTenant();
  const assistant = useAssistant();
  const [tab, setTab] = useState<'files' | 'deleted' | 'settings' | 'tech'>('files');
  const dev = data.device;
  const lrp = data.lastRecoveryPoint;
  const st = phoneStatus(dev);
  const owner = dev.owner?.name || dev.owner?.email;
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/devices`}>{arrow.back()} {tr('Computers and phones')}</Link>}
        title={dev.display_name}
        desc={<>{owner ? tr('{name}’s phone', { name: owner }) : tr('Phone')} · {systemName(dev)} · {st.seen}</>}
        actions={
          <>
            {t.can('restores.create') && lrp && <Link className="btn" to={`${t.base}/restore?device=${dev.id}`}>{tr('Get files back')}</Link>}
            {assistant.available && <Button variant="ghost" onClick={() => assistant.open(dev.id)}>{tr('Ask about this phone')}</Button>}
          </>
        }
      />
      <div className="grid g2">
        <Card title={tr('Files on this phone')}>
          <div style={{ marginBottom: 12 }}>
            <Badge tone={st.tone}>{st.label}</Badge>
            {st.hint && <div className="small muted" style={{ marginTop: 6 }}>{st.hint}</div>}
          </div>
          <p className="small muted" style={{ marginTop: 0 }}>{tr('Everything in “My Files” in the ZeroGap app is saved as soon as it is added. There is nothing to schedule.')}</p>
          <dl className="kv">
            <dt>{tr('Last saved')}</dt>
            <dd>{lrp ? <>{when(lrp.finished_at ?? lrp.committed_at)} <span className="muted small">({ago(lrp.finished_at ?? lrp.committed_at)})</span></> : tr('Not yet')}</dd>
            <dt>{tr('Files')}</dt>
            <dd>{lrp ? tr('{files} files · {size}', { files: Number(lrp.file_count).toLocaleString(locale()), size: bytes(lrp.total_bytes) }) : '—'}</dd>
            <dt>{tr('Deleted files')}</dt>
            <dd><DeletedFilesKept /></dd>
          </dl>
        </Card>
        <Card title={tr('Sent to computers')}>
          <PhoneMirrors deviceId={dev.id} />
        </Card>
      </div>
      {dev.status === 'quarantined' && <div className="notice bad" style={{ marginTop: 16 }}><strong>{tr('This phone is blocked')}</strong>{tr('It was reported lost or stolen {time}. It can no longer reach its files, but they can still be got back.', { time: ago(dev.quarantined_at) })}</div>}
      <div style={{ marginTop: 20 }}>
        <Tabs value={tab} onChange={setTab} tabs={[{ key: 'files', label: tr('Files') }, { key: 'deleted', label: tr('Recently deleted') }, { key: 'settings', label: tr('Settings') }, { key: 'tech', label: tr('Technical details') }]} />
      </div>
      {tab === 'files' && (lrp ? <RecoveryPointBrowser rpId={lrp.id} deviceId={dev.id} pathStyle="posix" rootLabel={tr('My Files')} /> : <Card><Empty glyph="◷" title={tr('No files yet')}>{tr('Files appear here once something is added in the app on the phone.')}</Empty></Card>)}
      {tab === 'deleted' && <PhoneDeleted deviceId={dev.id} />}
      {tab === 'settings' && <PhoneSettings dev={dev} onChanged={onChanged} />}
      {tab === 'tech' && <PhoneTech dev={dev} />}
    </div>
  );
}

/** "Can be got back for 30 days", and for an administrator the choice of the window the plan allows. */
function DeletedFilesKept() {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const s = useQuery({ queryKey: ['mobile-settings', t.tenantId], queryFn: () => get<{ retentionDays: number; maxDays: number; choices: number[] }>(`/api/t/${t.tenantId}/mobile/settings`) });
  if (!s.data) return <>—</>;
  const kept = tr('Can be got back for {n} days', { n: s.data.retentionDays });
  if (!t.can('settings.manage') || s.data.choices.length < 2) return <>{kept}</>;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <select
        aria-label={tr('How long deleted files can be got back')}
        value={s.data.retentionDays}
        onChange={async (e) => {
          try {
            await put(`/api/t/${t.tenantId}/mobile/settings`, { retentionDays: Number(e.target.value) });
            await qc.invalidateQueries({ queryKey: ['mobile-settings', t.tenantId] });
            toast(tr('Saved'));
          } catch (err) {
            toast(friendlyError(err), true);
          }
        }}
      >
        {s.data.choices.map((d) => <option key={d} value={d}>{tr('Can be got back for {n} days', { n: d })}</option>)}
      </select>
      <span className="small muted">{tr('The same for every phone in the organization.')}</span>
    </div>
  );
}

/** Where this phone's files appear. The person sets this up in the app, on the phone. */
function PhoneMirrors({ deviceId }: { deviceId: string }) {
  const t = useTenant();
  const allowed = t.can('mirrors.read');
  const m = useQuery({ queryKey: ['mirrors', deviceId], queryFn: () => get<{ mirrors: any[] }>(`/api/t/${t.tenantId}/mirrors?sourceDeviceId=${deviceId}`), enabled: allowed });
  if (!allowed) return <p className="muted small" style={{ margin: 0 }}>—</p>;
  if (m.isLoading) return <Loading />;
  if (m.error) return <ErrorBox error={m.error} />;
  const list = (m.data?.mirrors ?? []).filter((x) => x.state !== 'removed');
  if (!list.length) return <p className="muted" style={{ margin: 0 }}>{tr('Not sent to any computer. In the app, choose “Send to a computer”.')}</p>;
  return (
    <div className="stack" style={{ gap: 10 }}>
      {list.map((x) => (
        <div key={x.id}>
          <Link to={`${t.base}/devices/${x.destination_device_id}`}><strong>{x.destination_name}</strong></Link>
          <div className="small muted"><bdi className="mono">{x.resolved_path || x.destination_path}</bdi></div>
          <div className="small muted">{x.last_sync_at ? tr('Up to date as of {time}', { time: ago(x.last_sync_at) }) : tr('Waiting for the computer')}</div>
        </div>
      ))}
    </div>
  );
}

/** Files that are gone from the phone but can still be got back. */
function PhoneDeleted({ deviceId }: { deviceId: string }) {
  const t = useTenant();
  const q = useQuery({ queryKey: ['phone-deleted', deviceId], queryFn: () => get<{ files: any[] }>(`/api/t/${t.tenantId}/devices/${deviceId}/deleted?limit=200`) });
  if (q.isLoading) return <Card><Loading /></Card>;
  if (q.error) return <Card><ErrorBox error={q.error} /></Card>;
  const files = q.data?.files ?? [];
  if (!files.length) return <Card><Empty glyph="✓" title={tr('Nothing deleted recently')}>{tr('Files deleted on the phone are listed here while they can still be got back.')}</Empty></Card>;
  return (
    <div className="card table-wrap">
      <table>
        <thead><tr><th>{tr('Name')}</th><th className="right">{tr('Size')}</th><th>{tr('Deleted')}</th><th /></tr></thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.path}>
              <td><div style={{ fontWeight: 600 }}><bdi>{f.name}</bdi></div><div className="small muted"><bdi>{f.path}</bdi></div></td>
              <td className="right num small">{bytes(f.size)}</td>
              <td className="small">{f.deleted_after ? tr('After {time}', { time: when(f.deleted_after) }) : '—'}</td>
              <td className="right">
                {t.can('restores.create') && <Link className="btn sm" to={`${t.base}/restore?device=${deviceId}&rp=${f.recoveryPointId}&path=${encodeURIComponent(f.path)}`}>{tr('Get it back')}</Link>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PhoneSettings({ dev, onChanged }: { dev: any; onChanged: () => void }) {
  const t = useTenant();
  const toast = useToast();
  const [name, setName] = useState(dev.display_name);
  const [dialog, setDialog] = useState<null | 'block' | 'remove'>(null);
  const manage = t.can('devices.manage');
  return (
    <div className="grid g2">
      <Card title={tr('Name')}>
        <fieldset disabled={!manage} className="plain-fieldset">
          <Field label={tr('Name shown in ZeroGap')}><input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Field>
          <Button disabled={!name.trim() || name === dev.display_name} onClick={async () => {
            try {
              await patch(`/api/t/${t.tenantId}/devices/${dev.id}`, { displayName: name.trim() });
              toast(tr('Saved'));
              onChanged();
            } catch (e) {
              toast(friendlyError(e), true);
            }
          }}>{tr('Save')}</Button>
        </fieldset>
      </Card>
      <Card title={tr('Lost, stolen or replaced')}>
        <p className="small muted" style={{ marginTop: 0 }}>{tr('The files stay in ZeroGap and can still be got back, on a computer or a new phone.')}</p>
        <div className="stack" style={{ gap: 10, alignItems: 'flex-start' }}>
          {t.can('devices.credentials') && !['retired', 'quarantined'].includes(dev.status) && <Button variant="danger" onClick={() => setDialog('block')}>{tr('Lost or stolen? Block this phone…')}</Button>}
          {manage && dev.status !== 'retired' && <Button onClick={() => setDialog('remove')}>{tr('Remove this phone…')}</Button>}
          {dev.status === 'retired' && <p className="small" style={{ margin: 0 }}>{tr('This phone was removed {time}.', { time: ago(dev.retired_at) })}</p>}
        </div>
      </Card>
      {dialog === 'block' && (
        <ConfirmDialog title={tr('Block {name}?', { name: dev.display_name })} confirm={tr('Block the phone')} danger onClose={() => setDialog(null)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/devices/${dev.id}/quarantine`, { reason: tr('Reported lost or stolen') });
            setDialog(null);
            onChanged();
            toast(tr('Phone blocked'));
          }}>
          <p style={{ margin: 0 }}>{tr('The phone immediately loses all access to its files in ZeroGap and stops saving new ones. The files already saved stay safe.')}</p>
        </ConfirmDialog>
      )}
      {dialog === 'remove' && (
        <ConfirmDialog title={tr('Remove {name}?', { name: dev.display_name })} confirm={tr('Remove the phone')} onClose={() => setDialog(null)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/devices/${dev.id}/retire`, { reason: '' });
            setDialog(null);
            onChanged();
            toast(tr('Phone removed'));
          }}>
          <p style={{ margin: 0 }}>{tr('Use this when a phone is replaced or no longer used. It stops saving files and frees its place. Deleted files stay recoverable for the usual time.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function PhoneTech({ dev }: { dev: any }) {
  return (
    <div className="stack">
      <Card title={tr('Phone')}>
        <dl className="kv">
          <dt>{tr('System')}</dt><dd>{systemName(dev)}</dd>
          <dt>{tr('ZeroGap app version')}</dt><dd className="mono">{dev.agent_version || '—'}</dd>
          <dt>{tr('Connected since')}</dt><dd>{when(dev.enrolled_at)}</dd>
          <dt>{tr('Time zone')}</dt><dd>{dev.timezone ?? tr('unknown')}</dd>
          <dt>{tr('Device ID')}</dt><dd className="mono small">{dev.id}</dd>
        </dl>
      </Card>
      <Card title={tr('Event log')} pad={false}><DeviceEvents deviceId={dev.id} /></Card>
    </div>
  );
}
